import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "container-flex labs"
}
const _hoisted_2 = {
  key: 0,
  class: "labs__skeletonRecent"
}
const _hoisted_3 = {
  key: 1,
  class: "labs__empty"
}

import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import { ElSkeleton, ElSkeletonItem } from 'element-plus';
import { toast } from '@/utils';
import { catchErrorShow } from '@/utils/errors';

import { LabData, FilterButton } from '@/types/LabData';
import { ProductStateEnum } from '@/types/enums';
import { ErrorCodeEnum } from '@/types/httpResponses';

import LabsRecent from '@/components/Carousel/Recent.vue';
import TransitionFadeGroup from '@/components/TransitionFadeGroup.vue';
import TemporaryUnavailable from '@/router/Alerts/TemporaryUnavailable/index.vue';
import LabsFilter from './components/Filter.vue';
import LabsCategory from './components/Category.vue';
import SideMenu from './components/SideMenu.vue';
import CardSkeleton from './components/CardSkeleton.vue';

import services from './services';

// import fakeData from '@/assets/fakeData/products-license';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const { t } = useI18n();
const store = useStore();

const isPortalClosed = computed(() => store.getters['Common/isPortalClosed']);
const isSuAccessRole = computed(() => store.getters.isSuAccessRole);
const isUser = computed<boolean>(() => store.getters.isAuthed);
const isLoading = ref(false);
const products = ref<LabData[]>([]);

const searchFilter = ref('');
const filterSections = ref<Map<string, FilterButton>>(new Map());
const hasLicense = ref(false);
const countLicense = computed(() => {
  let count = 0;
  filteredProducts.value.forEach((arr, key) => {
    if (!isFiltersNotActive.value && !isActiveFilter(key)) {
      return;
    }
    count += arr.filter((item: LabData) => item.license_state).length;
  });
  return count;
});

const isFiltersNotActive = computed<boolean>(
  () => !Array.from(filterSections.value.values()).some(
    (filter: FilterButton) => filter.active
  )
);

const categoryName = (label: string) => filterSections.value?.get(label)?.name || '';

const filteredProducts = computed<Map<string, LabData[]>>(
  () => products.value.reduce(
    (acc, cur) => {
      if (searchFilter.value && !cur.nameLower.includes(searchFilter.value)) {
        return acc;
      }
      if (hasLicense.value && !cur.license_state) {
        return acc;
      }
      if (!acc.has(cur.section.shortname)) {
        acc.set(cur.section.shortname, [cur]);
      } else {
        acc.get(cur.section.shortname).push(cur);
      }
      return acc;
    },
    new Map()
  )
);

const filteredProductsArray = computed(
  () => Array.from(filteredProducts.value)
);

const isEmpty = computed(() => filteredProducts.value.size === 0);

const createFilterBtn = () => {
  filterSections.value = products.value.reduce((acc, cur: LabData) => {
    if (!acc.has(cur.section.shortname)) {
      acc.set(cur.section.shortname, {
        ...cur.section,
        active: false,
        count: computed(() => filteredProducts.value.get(cur.section.shortname)?.length || 0),
      });
    }
    return acc;
  }, new Map());
};

const getProducts = async () => {
  try {
    isLoading.value = true;

    const data = isUser.value
      ? await services.getLaboratoriesList()
      : await services.getPublicLaboratoriesList();

    products.value = data.reduce((acc: LabData[], product: LabData) => {
      acc.push({ ...product, nameLower: product.name.toLowerCase() });
      return acc;
    }, []);
    // products.value = fakeData;

    if (products.value.length > 0) {
      updatePublishedStatus();
      createFilterBtn();
    }

    if (isPortalClosed.value) {
      store.commit('Common/SET_IS_PORTAL_CLOSED', false);
    }
  } catch (err: any) {
    if (err.response.status === ErrorCodeEnum.BadRequest) {
      if (!isPortalClosed.value) {
        store.commit('Common/SET_IS_PORTAL_CLOSED', true);
      }

      return;
    }

    catchErrorShow(err, t);
  } finally {
    isLoading.value = false;
  }
};

const updateGroup = (key: string) => {
  if (filterSections.value?.get(key)) {
    const elem: FilterButton = filterSections.value?.get(key);
    elem.active = !elem.active;
  }
};

const updateSearchInput = (searchValue: string) => {
  searchFilter.value = searchValue.trim().toLowerCase();
};

const isActiveFilter = (label: string) => filterSections.value.get(label)?.active;

const updatePublishedStatus = () => {
  const wsData = store.getters['Common/wsLauncher'];
  if (!wsData) { return; }

  const lastProduct = getProductByID(wsData.id);
  if (!lastProduct) { return; }
  lastProduct.state = ProductStateEnum.Complete;

  if (wsData.successful) {
    lastProduct.enabled = true;
    toast.success(t('notifications.productPublishedSuccess', { name: lastProduct.name }));
  } else {
    lastProduct.enabled = false;
    const errorText = t('notifications.productPublishedError', { name: lastProduct.name });
    const errorDescription = wsData.error ? `<br/>${t('notifications.productPublishedErrorDescription', { error: wsData.error })}` : '';
    toast.error(errorText + errorDescription);
  }
  store.commit('Common/SET_WS_LAUNCHER', null);
};

watch(() => store.getters['Common/wsLauncher'], (data) => {
  if (!data) { return; }
  updatePublishedStatus();
});

const getProductByID = (id: number) => {
  if (!id) { return null; }
  return products.value
    .find((product: LabData) => product.id === id);
};

getProducts();


return (_ctx: any,_cache: any) => {
  return (isPortalClosed.value && !isSuAccessRole.value)
    ? (_openBlock(), _createBlock(TemporaryUnavailable, { key: 0 }))
    : (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (isLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createVNode(_unref(ElSkeleton), { animated: "" }, {
                template: _withCtx(() => [
                  _createVNode(_unref(ElSkeletonItem), {
                    class: "labs__skeletonRecent__item",
                    variant: "h3"
                  })
                ]),
                _: 1
              }),
              _createVNode(_unref(ElSkeleton), {
                count: 4,
                class: "labs__skeletonLastCards",
                loading: isLoading.value
              }, {
                template: _withCtx(() => [
                  _createVNode(CardSkeleton)
                ]),
                _: 1
              }, 8, ["loading"])
            ]))
          : _createCommentVNode("", true),
        _createVNode(LabsRecent, { products: products.value }, null, 8, ["products"]),
        _createVNode(LabsFilter, {
          filterSections: filterSections.value,
          isNotFound: isEmpty.value,
          hasLicense: hasLicense.value,
          countLicense: countLicense.value,
          "onUpdate:license": _cache[0] || (_cache[0] = ($event: any) => (hasLicense.value = $event)),
          "onUpdate:group": updateGroup,
          "onUpdate:search": updateSearchInput
        }, null, 8, ["filterSections", "isNotFound", "hasLicense", "countLicense"]),
        _createVNode(_unref(ElSkeleton), {
          animated: "",
          loading: isLoading.value,
          count: 4,
          class: "labs__skeletonFilter"
        }, {
          template: _withCtx(() => [
            _createVNode(_unref(ElSkeletonItem), {
              class: "labs__skeletonFilter__item",
              variant: "h3"
            })
          ]),
          _: 1
        }, 8, ["loading"]),
        (isEmpty.value && searchFilter.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(t)("labs.emptySearch")), 1))
          : _createCommentVNode("", true),
        _createVNode(_unref(ElSkeleton), {
          animated: "",
          loading: isLoading.value
        }, {
          template: _withCtx(() => [
            _createVNode(_unref(ElSkeletonItem), {
              class: "labs__skeletonTitle",
              variant: "h3"
            })
          ]),
          _: 1
        }, 8, ["loading"]),
        _createVNode(_unref(ElSkeleton), {
          count: 20,
          class: "labs__skeletonCards",
          animated: "",
          loading: isLoading.value
        }, {
          template: _withCtx(() => [
            _createVNode(CardSkeleton, {
              withFooter: "",
              withHeader: ""
            })
          ]),
          _: 1
        }, 8, ["loading"]),
        _createVNode(TransitionFadeGroup, { tag: "div" }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredProductsArray.value, ([label, categories]) => {
              return _withDirectives((_openBlock(), _createBlock(LabsCategory, {
                key: label,
                categories: categories
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(categoryName(label)), 1)
                ]),
                _: 2
              }, 1032, ["categories"])), [
                [_vShow, isFiltersNotActive.value || isActiveFilter(label)]
              ])
            }), 128))
          ]),
          _: 1
        }),
        (isSuAccessRole.value)
          ? (_openBlock(), _createBlock(SideMenu, { key: 2 }))
          : _createCommentVNode("", true)
      ]))
}
}

})